import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import ProductFormModal from '../components/ProductFormModal';
import ManageCategoriesModal from '../components/ManageCategoriesModal';
import MoveProductModal from '../components/MoveProductModal';
import { Search, Plus, Filter, Download, MoreVertical, Edit, Trash2, X, ChevronDown, ChevronRight, Eye, Package, Check, Settings, ArrowRight, FileText, Film, Image as ImageIcon } from 'lucide-react';
import { API_BASE_URL } from '../config/api.config';

const ProductManagement = () => {
  // State management
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [gstFilter, setGstFilter] = useState('all');
  const [dataFetched, setDataFetched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [showMoveProductModal, setShowMoveProductModal] = useState(false);
  const [productToMove, setProductToMove] = useState(null);

  // Prevent body scroll when any modal is open
  useEffect(() => {
    if (showViewModal || showAddModal || showEditModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [showViewModal, showAddModal, showEditModal]);

  // Form states
  const [currentStep, setCurrentStep] = useState(1); // 1: Supplier, 2: Product
  const [formTouched, setFormTouched] = useState(false);

  // Add Category states
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');

  const [newCategoryIsOthers, setNewCategoryIsOthers] = useState(false);
  const [showManageCategories, setShowManageCategories] = useState(false);

  // Complete form structure based on new specifications
  const [formData, setFormData] = useState({
    supplier: {
      shopName: '',
      contactPersonName: '',
      emailAddress: '',
      shopAddress: '',
      gstNumber: '',
      invoiceNumber: '',
      dateOfPurchase: '',
      paymentMode: '',
      additionalNotes: ''
    },
    product: {
      name: '',
      categoryId: '',
      description: '',
      sku: '',
      hsnCode: '',
      quantity: '',
      purchasePricePerUnit: '',
      sellingPrice: '',
      gstPercentage: '',
      customGstPercentage: '',
      dimensions: { length: '', width: '', height: '', unit: 'cm' },
      materialType: '',
      colorFinish: '',
      estimationCost: '',
      estimationUnit: '',
      installationFees: '',
      estimationNotes: '',
      installationNotes: '',
      hasVariants: false,
      variants: [],
      warranty: { hasWarranty: false, warrantyPeriod: '', warrantyDetails: '' },
      catalogs: []
    }
  });

  // Export form data
  const [exportData, setExportData] = useState({
    month: '',
    year: new Date().getFullYear(),
    gstPercentage: 'all',
    format: 'excel'
  });

  // Options for dropdowns
  const paymentModes = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Card', label: 'Card' },
    { value: 'UPI', label: 'UPI' },
    { value: 'Credit', label: 'Credit' },
    { value: 'Bank Transfer', label: 'Bank Transfer' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Other', label: 'Other' }
  ];

  const gstOptions = [
    { value: '0', label: '0%' },
    { value: '5', label: '5%' },
    { value: '12', label: '12%' },
    { value: '18', label: '18%' },
    { value: '28', label: '28%' }
  ];

  // Fetch data on component mount
  useEffect(() => {
    if (!dataFetched) {
      setDataFetched(true);
      fetchData();
    }
  }, [dataFetched]);

  // Filter products when filters change
  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, categoryFilter, gstFilter]);

  const fetchData = async (retryCount = 0) => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get('/admin/products'),
        axios.get('/admin/categories')
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to load products data:', error);

      // Fallback sample data
      const sampleProducts = [{
        id: 1,
        supplier: {
          shopName: 'ABC Furniture Suppliers',
          contactPersonName: 'Rajesh Kumar',
          invoiceNumber: 'INV-2025-001',
          dateOfPurchase: '2025-11-01',
          paymentMode: 'UPI'
        },
        product: {
          name: 'Office Chair Deluxe',
          sku: 'OFC-CHR-001',
          quantity: 10,
          purchasePricePerUnit: 2500,
          sellingPrice: 3500,
          gstPercentage: 18
        },
        calculations: {
          gstAmount: 4500,
          totalAmountWithGst: 29500
        }
      }];
      setProducts(sampleProducts);
      setCategories([
        { id: 1, name: 'Furniture' },
        { id: 2, name: 'Lighting' },
        { id: 3, name: 'Decor' }
      ]);
      toast('Using sample data - API connection failed', { icon: '⚠️' });
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    console.log('🔍 Filtering products with:', {
      totalProducts: products.length,
      searchTerm,
      categoryFilter,
      gstFilter
    });

    let filtered = products;

    if (searchTerm) {
      console.log('🔍 Applying search filter:', searchTerm);
      filtered = filtered.filter(product => {
        const nameMatch = product.product?.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const skuMatch = product.product?.sku?.toLowerCase().includes(searchTerm.toLowerCase());
        const supplierMatch = product.supplier?.shopName?.toLowerCase().includes(searchTerm.toLowerCase());

        console.log('🔍 Product search check:', {
          productName: product.product?.name,
          nameMatch,
          skuMatch,
          supplierMatch,
          finalMatch: nameMatch || skuMatch || supplierMatch
        });

        return nameMatch || skuMatch || supplierMatch;
      });
      console.log('🔍 After search filter:', filtered.length);
    }

    if (categoryFilter !== 'all') {
      console.log('🔍 Applying category filter:', categoryFilter);
      const beforeCount = filtered.length;
      filtered = filtered.filter(product => {
        // Try multiple matching strategies for category
        const productCategoryId = product.product?.categoryId;
        const productCategoryName = product.product?.categoryName;

        // Convert both to strings for comparison
        const categoryIdMatch = String(productCategoryId) === String(categoryFilter);

        // Also try matching by category name as fallback
        const selectedCategoryName = categories.find(cat => cat.id === categoryFilter)?.name;
        const categoryNameMatch = productCategoryName === selectedCategoryName;

        const match = categoryIdMatch || categoryNameMatch;

        console.log('🔍 Category check:', {
          productName: product.product?.name,
          productCategoryId: productCategoryId,
          productCategoryName: productCategoryName,
          filterCategoryId: categoryFilter,
          selectedCategoryName: selectedCategoryName,
          categoryIdMatch,
          categoryNameMatch,
          finalMatch: match
        });

        return match;
      });
      console.log('🔍 After category filter:', filtered.length, 'from', beforeCount);
    }

    if (gstFilter !== 'all') {
      console.log('🔍 Applying GST filter:', gstFilter);
      const beforeCount = filtered.length;
      filtered = filtered.filter(product => {
        const match = product.product?.gstPercentage === parseInt(gstFilter);
        console.log('🔍 GST check:', {
          productName: product.product?.name,
          productGST: product.product?.gstPercentage,
          filterGST: parseInt(gstFilter),
          match
        });
        return match;
      });
      console.log('🔍 After GST filter:', filtered.length, 'from', beforeCount);
    }

    console.log('🔍 Final filtered products:', filtered.length);
    setFilteredProducts(filtered);
  };

  // Export functionality
  const exportToCSV = () => {
    console.log('📊 Starting CSV export...');

    if (filteredProducts.length === 0) {
      toast.error('No products to export');
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Product Name',
      'SKU',
      'Category',
      'Quantity',
      'Purchase Price',
      'Selling Price',
      'GST %',
      'Supplier Name',
      'Supplier Contact',
      'Invoice Number',
      'Purchase Date',
      'Payment Mode'
    ];

    // Prepare CSV data
    const csvData = filteredProducts.map(product => [
      product.product?.name || '',
      product.product?.sku || '',
      product.product?.categoryName || 'N/A',
      product.product?.quantity || 0,
      product.product?.purchasePricePerUnit || 0,
      product.product?.sellingPrice || 0,
      product.product?.gstPercentage || 0,
      product.supplier?.shopName || '',
      product.supplier?.contactNumber || '',
      product.supplier?.invoiceNumber || '',
      product.supplier?.dateOfPurchase || '',
      product.supplier?.paymentMode || ''
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('✅ CSV export completed');
    toast.success(`Exported ${filteredProducts.length} products to CSV`);
    setShowExportModal(false);
  };

  // Resolve pending category ID
  const resolveCategory = async (categoryId) => {
    if (!categoryId) return null;
    if (!String(categoryId).startsWith('temp_')) return categoryId;

    console.log('🔄 Resolving pending category:', categoryId);
    const pendingCat = categories.find(c => c.id === categoryId);

    if (!pendingCat) {
      console.warn('⚠️ Pending category not found in state:', categoryId);
      return null;
    }

    try {
      const response = await axios.post('/admin/categories', {
        name: pendingCat.name,
        description: pendingCat.description,
        isOthers: pendingCat.isOthers || false
      });

      console.log('✅ Resolved category created:', response.data.id);
      // Update categories state with the real ID to prevent re-creation if user submits again
      setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, id: response.data.id, isPending: false } : c));

      return response.data.id;
    } catch (error) {
      console.error('❌ Failed to resolve pending category:', error);
      throw new Error(`Failed to create category "${pendingCat.name}": ${error.response?.data?.error || error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier: {
        shopName: '',
        contactPersonName: '',
        contactNumber: '',
        emailAddress: '',
        shopAddress: '',
        gstNumber: '',
        invoiceNumber: '',
        dateOfPurchase: '',
        paymentMode: '',
        additionalNotes: ''
      },
      product: {
        name: '', categoryId: '', description: '', sku: '', hsnCode: '', quantity: '',
        purchasePricePerUnit: '', sellingPrice: '', gstPercentage: '18', customGstPercentage: '',
        dimensions: { length: '', width: '', height: '', unit: 'cm' },
        materialType: '', colorFinish: '',
        estimationCost: '', estimationUnit: '',
        installationFees: '',
        estimationNotes: '',
        installationNotes: '',
        hasVariants: false,
        variants: [],
        warranty: { hasWarranty: false, warrantyPeriod: '', warrantyDetails: '' },
        catalogs: []
      }
    });
    setCurrentStep(1);
    setFormTouched(false);
    setEditingProduct(null);
    // Remove any pending categories from the list
    setCategories(prev => prev.filter(c => !c.isPending));
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setFormTouched(true);

    console.log('=== FORM VALIDATION DEBUG ===');
    console.log('Current formData:', JSON.stringify(formData, null, 2));

    // All fields are optional (temporary) - allow saving with any combination of data
    // Just validate custom GST if selected
    if (formData.product.gstPercentage === 'custom') {
      const customGst = formData.product.customGstPercentage?.toString().trim();
      console.log('Custom GST validation:', {
        gstPercentage: formData.product.gstPercentage,
        customGstPercentage: formData.product.customGstPercentage,
        customGstTrimmed: customGst,
        isNaN: isNaN(parseFloat(customGst))
      });

      if (!customGst || customGst === '' || isNaN(parseFloat(customGst)) || parseFloat(customGst) < 0) {
        toast.error('Please enter a valid custom GST percentage (0 or higher)');
        return;
      }
    }

    try {
      // Resolve category ID if it's a pending one
      const resolvedCategoryId = await resolveCategory(formData.product.categoryId);

      // Create FormData to handle file uploads
      const data = new FormData();

      const cleanProductData = {
        supplier: {
          shopName: formData.supplier.shopName?.trim() || '',
          contactPersonName: formData.supplier.contactPersonName?.trim() || '',
          contactNumber: formData.supplier.contactNumber?.trim() || '',
          emailAddress: formData.supplier.emailAddress?.trim() || '',
          shopAddress: formData.supplier.shopAddress?.trim() || '',
          gstNumber: formData.supplier.gstNumber?.trim() || '',
          invoiceNumber: formData.supplier.invoiceNumber?.trim() || '',
          dateOfPurchase: formData.supplier.dateOfPurchase || null,
          paymentMode: formData.supplier.paymentMode || 'Cash',
          additionalNotes: formData.supplier.additionalNotes || ''
        },
        product: {
          name: formData.product.name?.trim() || '',
          categoryId: resolvedCategoryId,
          description: formData.product.description || '',
          sku: formData.product.sku?.trim().toUpperCase() || '',
          hsnCode: (formData.product.hsnCode?.trim() || '').toUpperCase(),
          quantity: parseInt(formData.product.quantity) || 0,
          purchasePricePerUnit: parseFloat(formData.product.purchasePricePerUnit) || 0,
          sellingPrice: parseFloat(formData.product.sellingPrice) || 0,
          gstPercentage: (formData.product.gstPercentage === 'custom')
            ? ((formData.product.customGstPercentage !== '' && !isNaN(parseFloat(formData.product.customGstPercentage))) ? parseFloat(formData.product.customGstPercentage) : 18)
            : ((formData.product.gstPercentage !== '' && !isNaN(parseFloat(formData.product.gstPercentage))) ? parseFloat(formData.product.gstPercentage) : 18),
          dimensions: {
            length: parseFloat(formData.product.dimensions?.length) || 0,
            width: parseFloat(formData.product.dimensions?.width) || 0,
            height: parseFloat(formData.product.dimensions?.height) || 0,
            unit: formData.product.dimensions?.unit || 'cm'
          },
          materialType: formData.product.materialType || '',
          colorFinish: formData.product.colorFinish || '',
          estimationCost: parseFloat(formData.product.estimationCost) || 0,
          estimationUnit: formData.product.estimationUnit || 'SFT',
          installationFees: parseFloat(formData.product.installationFees) || 0,
          estimationNotes: formData.product.estimationNotes || '',
          installationNotes: formData.product.installationNotes || '',
          hasVariants: formData.product.hasVariants || false,
          variants: (formData.product.variants || []).map(v => ({
            ...v,
            purchasePrice: parseFloat(v.purchasePrice) || 0,
            sellingPrice: parseFloat(v.sellingPrice) || 0,
            estimationCost: parseFloat(v.estimationCost) || 0,
            installationFees: parseFloat(v.installationFees) || 0,
            quantity: parseInt(v.quantity) || 0
          })),
          warranty: {
            hasWarranty: formData.product.warranty?.hasWarranty || false,
            warrantyPeriod: parseInt(formData.product.warranty?.warrantyPeriod) || 0,
            warrantyDetails: formData.product.warranty?.warrantyDetails || ''
          }
        }
      };

      data.append('supplier', JSON.stringify(cleanProductData.supplier));
      data.append('product', JSON.stringify(cleanProductData.product));

      // Append files
      if (formData.product.catalogs && formData.product.catalogs.length > 0) {
        formData.product.catalogs.forEach((file) => {
          if (file instanceof File) {
            data.append('catalogs', file);
          }
        });
      }

      console.log('=== SENDING TO SERVER (FORMDATA) ===');

      const response = await axios.post('/admin/products', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('=== SUCCESS ===');
      toast.success('Product added successfully!');
      fetchData(); // Refresh to get all fields properly
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to add product:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to add product');
    }
  };

  const handleViewProduct = (product) => {
    setViewingProduct(product);
    setShowViewModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    // Populate form with existing data
    setFormData({
      supplier: {
        shopName: product.supplier?.shopName || '',
        contactPersonName: product.supplier?.contactPersonName || '',
        contactNumber: product.supplier?.contactNumber || '',
        emailAddress: product.supplier?.emailAddress || '',
        shopAddress: product.supplier?.shopAddress || '',
        gstNumber: product.supplier?.gstNumber || '',
        invoiceNumber: product.supplier?.invoiceNumber || '',
        dateOfPurchase: product.supplier?.dateOfPurchase ? new Date(product.supplier.dateOfPurchase).toISOString().split('T')[0] : '',
        paymentMode: product.supplier?.paymentMode || '',
        additionalNotes: product.supplier?.additionalNotes || ''
      },
      product: {
        name: product.product?.name || '',
        categoryId: product.product?.categoryId || '',
        sku: product.product?.sku || '',
        hsnCode: product.product?.hsnCode || '',
        description: product.product?.description || '',
        purchasePricePerUnit: product.product?.purchasePricePerUnit || '',
        sellingPrice: product.product?.sellingPrice || '',
        quantity: product.product?.quantity || '',
        gstPercentage: product.product?.gstPercentage || '18',
        customGstPercentage: '',
        dimensions: product.product?.dimensions || { length: '', width: '', height: '', unit: 'cm' },
        materialType: product.product?.materialType || '',
        colorFinish: product.product?.colorFinish || '',
        estimationCost: product.product?.estimationCost || '',
        estimationUnit: product.product?.estimationUnit || '',
        installationFees: product.product?.installationFees || '',
        estimationNotes: product.product?.estimationNotes || '',
        installationNotes: product.product?.installationNotes || '',
        hasVariants: product.product?.hasVariants || false,
        variants: product.product?.variants || [],
        warranty: product.product?.warranty || { hasWarranty: false, warrantyPeriod: '', warrantyDetails: '' },
        status: product.product?.status || 'active',
        catalogs: product.product?.catalogs || []
      }
    });
    setShowEditModal(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;

    try {
      // Resolve category ID if it's a pending one
      const resolvedCategoryId = await resolveCategory(formData.product.categoryId);

      // Create FormData to handle file uploads
      const data = new FormData();

      const cleanProductData = {
        supplier: {
          shopName: formData.supplier.shopName?.trim() || '',
          contactPersonName: formData.supplier.contactPersonName?.trim() || '',
          contactNumber: formData.supplier.contactNumber?.trim() || '',
          emailAddress: formData.supplier.emailAddress?.trim() || '',
          shopAddress: formData.supplier.shopAddress?.trim() || '',
          gstNumber: formData.supplier.gstNumber?.trim() || '',
          invoiceNumber: formData.supplier.invoiceNumber?.trim() || '',
          dateOfPurchase: formData.supplier.dateOfPurchase || null,
          paymentMode: formData.supplier.paymentMode || 'Cash',
          additionalNotes: formData.supplier.additionalNotes || ''
        },
        product: {
          name: formData.product.name?.trim() || '',
          categoryId: resolvedCategoryId,
          description: formData.product.description || '',
          sku: formData.product.sku?.trim().toUpperCase() || '',
          hsnCode: (formData.product.hsnCode?.trim() || '').toUpperCase(),
          quantity: parseInt(formData.product.quantity) || 0,
          purchasePricePerUnit: parseFloat(formData.product.purchasePricePerUnit) || 0,
          sellingPrice: parseFloat(formData.product.sellingPrice) || 0,
          gstPercentage: (formData.product.gstPercentage === 'custom')
            ? ((formData.product.customGstPercentage !== '' && !isNaN(parseFloat(formData.product.customGstPercentage))) ? parseFloat(formData.product.customGstPercentage) : 18)
            : ((formData.product.gstPercentage !== '' && !isNaN(parseFloat(formData.product.gstPercentage))) ? parseFloat(formData.product.gstPercentage) : 18),
          dimensions: {
            length: parseFloat(formData.product.dimensions?.length) || 0,
            width: parseFloat(formData.product.dimensions?.width) || 0,
            height: parseFloat(formData.product.dimensions?.height) || 0,
            unit: formData.product.dimensions?.unit || 'cm'
          },
          materialType: formData.product.materialType || '',
          colorFinish: formData.product.colorFinish || '',
          estimationCost: parseFloat(formData.product.estimationCost) || 0,
          estimationUnit: formData.product.estimationUnit || 'SFT',
          installationFees: parseFloat(formData.product.installationFees) || 0,
          estimationNotes: formData.product.estimationNotes || '',
          installationNotes: formData.product.installationNotes || '',
          hasVariants: formData.product.hasVariants || false,
          variants: (formData.product.variants || []).map(v => ({
            ...v,
            purchasePrice: parseFloat(v.purchasePrice) || 0,
            sellingPrice: parseFloat(v.sellingPrice) || 0,
            estimationCost: parseFloat(v.estimationCost) || 0,
            installationFees: parseFloat(v.installationFees) || 0,
            quantity: parseInt(v.quantity) || 0
          })),
          warranty: {
            hasWarranty: formData.product.warranty?.hasWarranty || false,
            warrantyPeriod: parseInt(formData.product.warranty?.warrantyPeriod) || 0,
            warrantyDetails: formData.product.warranty?.warrantyDetails || ''
          },
          catalogs: formData.product.catalogs.filter(c => !(c instanceof File)) // Keep existing ones
        }
      };

      data.append('supplier', JSON.stringify(cleanProductData.supplier));
      data.append('product', JSON.stringify(cleanProductData.product));

      // Append new files
      if (formData.product.catalogs) {
        formData.product.catalogs.forEach((file) => {
          if (file instanceof File) {
            data.append('catalogs', file);
          }
        });
      }

      console.log('=== UPDATING PRODUCT (FORMDATA) ===');

      const response = await axios.put(`/admin/products/${editingProduct.id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('=== UPDATE SUCCESS ===');
      toast.success('Product updated successfully!');
      fetchData(); // Refresh to get all fields properly
      setShowEditModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to update product');
    }
  };


  // Handle Add Category
  const handleAddCategory = async (name, description, isOthers) => {
    try {
      if (isOthers) {
        // Immediate creation for "Others"
        const response = await axios.post('/admin/categories', {
          name,
          description,
          isOthers: true
        });

        const newCategory = { id: response.data.id, name, description, isOthers: true };
        setCategories([...categories, newCategory]);

        // Close the modal and reset form since "Others" category is often for manual entry in other flows
        setShowAddModal(false);
        resetForm();
        setNewCategoryName('');
        setNewCategoryDescription('');
        setNewCategoryIsOthers(false);
        toast.success('Category "Others" created successfully!');
      } else {
        // Pending creation for Normal categories
        const tempId = `temp_${Date.now()}`;
        const pendingCat = { id: tempId, name, description, isOthers: false, isPending: true };

        // We add it to categories list locally so it appears in dropdown
        setCategories([...categories, pendingCat]);

        // Auto-select
        setFormData(prev => ({
          ...prev,
          product: { ...prev.product, categoryId: tempId }
        }));

        setShowAddCategory(false);
        setNewCategoryName('');
        setNewCategoryDescription('');
        setNewCategoryIsOthers(false);
        toast.success('Category queued for creation upon product save.');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      const errorMsg = error.response?.data?.error || 'Failed to add category';
      toast.error(errorMsg);
    }
  };





  // Handle Move & Delete Product
  const handleMoveProductClick = (product) => {
    setProductToMove(product);
    setShowMoveProductModal(true);
  };

  const handleMoveSuccess = () => {
    fetchData();
    setShowMoveProductModal(false);
    setProductToMove(null);
  };

  const handleDeleteProductClick = async (product) => {
    if (window.confirm(`Are you sure you want to delete "${product.product?.name || 'this product'}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`/admin/products/${product.id || product._id}`);
        toast.success('Product deleted successfully');
        fetchData(); // Refresh list
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  // Handle SKU Generation



  // View Product Modal
  const ProductViewModal = () => {
    if (!viewingProduct) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex justify-between items-start p-6 border-b flex-shrink-0">
            <h3 className="text-xl font-bold text-[#0A5F38]">{viewingProduct.product?.name}</h3>
            <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Product Details Section */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-[#0A5F38] border-b pb-2 mb-4">Product Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Category</label>
                      <p className="text-slate-900 font-medium">{viewingProduct.product?.categoryName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">HSN Code</label>
                      <p className="text-slate-900 font-medium">{viewingProduct.product?.hsnCode || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Purchase Price</label>
                      <p className="text-slate-900 font-medium">₹{viewingProduct.product?.purchasePricePerUnit?.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Selling Price</label>
                      <p className="text-slate-900 font-medium">₹{viewingProduct.product?.sellingPrice?.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">GST %</label>
                      <p className="text-slate-900 font-medium">{viewingProduct.product?.gstPercentage}%</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Material</label>
                      <p className="text-slate-900 font-medium">{viewingProduct.product?.materialType || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Color/Finish</label>
                      <p className="text-slate-900 font-medium">{viewingProduct.product?.colorFinish || '-'}</p>
                    </div>
                    <div className="col-span-1">
                      <label className="text-sm font-medium text-gray-500">Estimation Cost</label>
                      <p className="text-slate-900 font-medium">{viewingProduct.product?.estimationCost ? `₹${viewingProduct.product.estimationCost.toLocaleString()} per ${viewingProduct.product.estimationUnit || 'unit'}` : '-'}</p>
                      {viewingProduct.product?.estimationNotes && (
                        <p className="text-xs text-[#0A5F38] mt-1 italic font-medium">Nota: {viewingProduct.product.estimationNotes}</p>
                      )}
                    </div>
                    <div className="col-span-1">
                      <label className="text-sm font-medium text-gray-500">Installation Fees</label>
                      <p className="text-slate-900 font-medium">{viewingProduct.product?.installationFees ? `₹${viewingProduct.product.installationFees.toLocaleString()}` : '₹0'}</p>
                      {viewingProduct.product?.installationNotes && (
                        <p className="text-xs text-[#0A5F38] mt-1 italic font-medium">Nota: {viewingProduct.product.installationNotes}</p>
                      )}
                    </div>
                  </div>

                  {/* Variant Details Table */}
                  {viewingProduct.product?.hasVariants && viewingProduct.product?.variants?.length > 0 && (
                    <div className="mt-8">
                      <h4 className="text-lg font-semibold text-[#0A5F38] border-b pb-2 mb-4">Product Variants</h4>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">{viewingProduct.product.variants[0]?.attributeName || 'Variant'} Value</th>
                              <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Price (₹)</th>
                              <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Installation (₹)</th>
                              <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Estimation (₹)</th>
                              <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Total (₹)</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {viewingProduct.product.variants.map((variant, idx) => (
                              <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 text-sm font-medium text-[#0A5F38] text-center">{variant.attributeValue}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-semibold text-center">{variant.sellingPrice?.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-semibold text-center">{variant.installationFees?.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-semibold text-center">{variant.estimationCost?.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-bold text-[#0A5F38] text-center">{variant.totalPrice?.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Dimensions */}
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-500 block mb-1">Dimensions</label>
                    <p className="text-slate-900 font-medium">
                      {viewingProduct.product?.dimensions?.length || 0} x {viewingProduct.product?.dimensions?.width || 0} x {viewingProduct.product?.dimensions?.height || 0} {viewingProduct.product?.dimensions?.unit || 'cm'}
                    </p>
                  </div>

                  {/* Description */}
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-500 block mb-1">Description</label>
                    <p className="text-slate-900">{viewingProduct.product?.description || 'No description provided.'}</p>
                  </div>

                  {/* Warranty */}
                  {viewingProduct.product?.warranty?.hasWarranty && (
                    <div className="mt-4 bg-green-50 p-3 rounded-lg border border-green-100">
                      <div className="flex items-start">
                        <Check className="h-5 w-5 text-green-600 mt-0.5 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-green-800">Under Warranty</p>
                          <p className="text-sm text-green-700">Period: {viewingProduct.product?.warranty?.warrantyPeriod} months</p>
                          <p className="text-sm text-green-700">{viewingProduct.product?.warranty?.warrantyDetails}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Supplier Details Section */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-[#0A5F38] border-b pb-2 mb-4">Supplier Information</h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500">Shop Name</label>
                        <p className="text-slate-900 font-medium text-lg">{viewingProduct.supplier?.shopName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Contact Person</label>
                        <p className="text-slate-900 font-medium">{viewingProduct.supplier?.contactPersonName || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <p className="text-slate-900 font-medium">{viewingProduct.supplier?.emailAddress || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500">Address</label>
                        <p className="text-slate-900 font-medium">{viewingProduct.supplier?.shopAddress || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">GST Number</label>
                        <p className="text-slate-900 font-medium">{viewingProduct.supplier?.gstNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Invoice Number</label>
                        <p className="text-slate-900 font-medium">{viewingProduct.supplier?.invoiceNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Purchase Date</label>
                        <p className="text-slate-900 font-medium">
                          {viewingProduct.supplier?.dateOfPurchase
                            ? new Date(viewingProduct.supplier.dateOfPurchase).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Payment Mode</label>
                        <p className="text-slate-900 font-medium">{viewingProduct.supplier?.paymentMode || 'N/A'}</p>
                      </div>
                    </div>

                    {viewingProduct.supplier?.additionalNotes && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-gray-500 block mb-1">Notes</label>
                        <p className="text-slate-900 bg-gray-50 p-3 rounded italic">{viewingProduct.supplier.additionalNotes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Multimedia Catalogs Section */}
            {viewingProduct.product?.catalogs && viewingProduct.product.catalogs.length > 0 && (
              <div className="mt-8 pt-6 border-t">
                <h4 className="text-lg font-semibold text-[#0A5F38] mb-4 flex items-center">
                  <ImageIcon className="h-5 w-5 mr-2" />
                  Multimedia Catalogs & Media
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {viewingProduct.product.catalogs.map((catalog, idx) => (
                    <div key={idx} className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all">
                      <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
                        {catalog.fileType === 'image' ? (
                          <img
                            src={`${API_BASE_URL}${catalog.url}`}
                            alt={catalog.originalName}
                            className="w-full h-full object-cover transition-transform group-hover:scale-110"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found'; }}
                          />
                        ) : catalog.fileType === 'video' ? (
                          <div className="flex flex-col items-center text-slate-400">
                            <Film className="h-10 w-10 mb-2" />
                            <span className="text-xs font-bold uppercase">Video Content</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center text-slate-400">
                            <FileText className="h-10 w-10 mb-2" />
                            <span className="text-xs font-bold uppercase">PDF Document</span>
                          </div>
                        )}

                        {/* Overlay Actions */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-3">
                          <a
                            href={`${API_BASE_URL}${catalog.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white rounded-full text-[#0A5F38] hover:bg-[#0A5F38] hover:text-white transition-colors"
                            title="View / Download"
                          >
                            <Download className="h-5 w-5" />
                          </a>
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-bold text-slate-800 truncate">{catalog.originalName || 'Untitled Asset'}</p>
                        <p className="text-[11px] text-slate-500 uppercase font-bold mt-1 tracking-wider">
                          {catalog.fileType} • {catalog.uploadedAt ? new Date(catalog.uploadedAt).toLocaleDateString() : 'Recent'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t bg-gray-50 rounded-b-lg flex justify-end">
            <button
              onClick={() => setShowViewModal(false)}
              className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-end items-center">
        <div className="flex space-x-3">
          <button
            onClick={() => setShowManageCategories(true)}
            className="bg-white border-2 border-[#C5A57D] text-[#0A5F38] px-6 py-2.5 rounded-lg font-bold hover:bg-[#C5A57D]/10 transition-colors flex items-center space-x-2"
          >
            <Settings className="h-4 w-4" />
            <span>View Categories</span>
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-white border-2 border-[#C5A57D] text-[#0A5F38] px-6 py-2.5 rounded-lg font-bold hover:bg-[#C5A57D]/10 transition-colors flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="bg-white border-2 border-[#C5A57D] text-[#0A5F38] px-6 py-2.5 rounded-lg font-bold hover:bg-[#C5A57D]/10 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Product</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search products, SKU, or supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field"
            />
          </div>

          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="input-field">
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>

          <select value={gstFilter} onChange={(e) => setGstFilter(e.target.value)} className="input-field">
            <option value="all">All GST Rates</option>
            {gstOptions.map(gst => (
              <option key={gst.value} value={gst.value}>{gst.label}</option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('all');
              setGstFilter('all');
              console.log('🔄 Filters cleared');
            }}
            className="btn-secondary flex items-center space-x-2 justify-center"
          >
            <X className="h-4 w-4" />
            <span>Clear Filters</span>
          </button>

          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Package className="h-4 w-4" />
            <span>{filteredProducts.length} Products</span>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-white">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchase</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(() => {
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

              return paginatedProducts.map((product) => (
                <tr key={product.id} className="hover:bg-white">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-[#0A5F38]">{product.product?.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 font-medium">{product.supplier?.shopName}</div>
                    <div className="text-sm text-gray-500">{product.supplier?.contactPersonName}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-gray-900">
                      {product.product?.hasVariants && product.product?.variants?.length > 0 ? (
                        <span className="text-[#0A5F38]">
                          ₹{Math.min(...product.product.variants.map(v => v.sellingPrice || 0)).toLocaleString()} - ₹{Math.max(...product.product.variants.map(v => v.sellingPrice || 0)).toLocaleString()}
                          <div className="text-[10px] text-gray-500 font-normal uppercase tracking-wider mt-0.5">{product.product.variants.length} Variants</div>
                        </span>
                      ) : (
                        `₹${product.product?.sellingPrice?.toLocaleString() || 0}`
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 font-normal uppercase tracking-wider mt-0.5">PURCHASE: ₹{product.product?.purchasePricePerUnit?.toLocaleString() || 0}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-[#C5A57D]/20 text-[#0A5F38]">
                      {product.product?.gstPercentage}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewProduct(product)}
                        className="text-[#0A5F38] hover:text-[#0A5F38]/80"
                        title="View Product"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="text-[#C5A57D] hover:text-[#B8985E]"
                        title="Edit Product"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMoveProductClick(product)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Move to Category"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProductClick(product)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete Product"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {filteredProducts.length > itemsPerPage && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-slate-100 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(filteredProducts.length / itemsPerPage), currentPage + 1))}
                disabled={currentPage === Math.ceil(filteredProducts.length / itemsPerPage)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredProducts.length)}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredProducts.length)}</span> of <span className="font-medium">{filteredProducts.length}</span> results
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Items per page:</span>
                <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500">
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"><ChevronDown className="h-5 w-5 rotate-90" /></button>
                  {(() => { const totalPages = Math.ceil(filteredProducts.length / itemsPerPage); const pages = []; for (let i = 1; i <= Math.min(totalPages, 5); i++) { pages.push(<button key={i} onClick={() => setCurrentPage(i)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-white'}`}>{i}</button>); } return pages; })()}
                  <button onClick={() => setCurrentPage(Math.min(Math.ceil(filteredProducts.length / itemsPerPage), currentPage + 1))} disabled={currentPage === Math.ceil(filteredProducts.length / itemsPerPage)} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"><ChevronDown className="h-5 w-5 -rotate-90" /></button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Manage Categories Modal */}
      {showManageCategories && (
        <ManageCategoriesModal
          categories={categories}
          setCategories={setCategories}
          onClose={() => setShowManageCategories(false)}
          products={products}
          onCategoriesChange={() => fetchData()} // Modified to just call fetchData
          onProductsChange={fetchData} // New prop for product updates (move/delete)
        />
      )}

      {/* Move Product Modal */}
      {showMoveProductModal && (
        <MoveProductModal
          isOpen={showMoveProductModal}
          onClose={() => setShowMoveProductModal(false)}
          product={productToMove}
          categories={categories}
          onMoveSuccess={handleMoveSuccess}
        />
      )}

      {/* View Product Modal */}
      {showViewModal && viewingProduct && <ProductViewModal />}

      {/* Product Form Modal */}
      <ProductFormModal
        show={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        formData={formData}
        setFormData={setFormData}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        onSubmit={handleAddProduct}
        categories={categories}
        editing={!!editingProduct}
        formTouched={formTouched}
        showAddCategory={showAddCategory}
        setShowAddCategory={setShowAddCategory}
        newCategoryName={newCategoryName}
        setNewCategoryName={setNewCategoryName}
        newCategoryDescription={newCategoryDescription}
        setNewCategoryDescription={setNewCategoryDescription}
        newCategoryIsOthers={newCategoryIsOthers}
        setNewCategoryIsOthers={setNewCategoryIsOthers}
        handleAddCategory={handleAddCategory}
      />

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Export Products</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p>Export {filteredProducts.length} products to CSV format</p>
                <p className="mt-2">The export will include:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Product details (name, SKU, category)</li>
                  <li>Pricing information</li>
                  <li>Supplier information</li>
                  <li>Purchase details</li>
                </ul>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={exportToCSV}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Download CSV</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <ProductFormModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingProduct(null);
            resetForm();
          }}
          formData={formData}
          setFormData={setFormData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          onSubmit={handleUpdateProduct}
          categories={categories}
          editing={true}
          formTouched={formTouched}
          showAddCategory={showAddCategory}
          setShowAddCategory={setShowAddCategory}
          newCategoryName={newCategoryName}
          setNewCategoryName={setNewCategoryName}
          newCategoryDescription={newCategoryDescription}
          setNewCategoryDescription={setNewCategoryDescription}
          newCategoryIsOthers={newCategoryIsOthers}
          setNewCategoryIsOthers={setNewCategoryIsOthers}
          handleAddCategory={handleAddCategory}
        />
      )}
    </div>
  );
};

export default ProductManagement;
